import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import Lenis from 'lenis';

/**
 * SmoothScroll — défilement inertiel « premium » (Lenis) sur tout le site.
 *
 * C'est l'outil qui donne le feel haut de gamme au scroll (inertie douce,
 * cohérente avec les reveals/parallaxe). Garde-fous :
 *   - COUPÉ sous prefers-reduced-motion (scroll natif, accessibilité).
 *   - Pas de smooth sur le tactile (le momentum natif mobile reste meilleur).
 *   - Ancres internes (#id) routées via lenis.scrollTo (offset header sticky).
 *   - Reset immédiat en haut à chaque changement de route (SPA).
 *   - Expose window.__lenis pour les composants qui veulent piloter le scroll.
 *
 * La CSS requise par Lenis (classe .lenis sur <html>) est dans index.css.
 */
export default function SmoothScroll() {
  const reduce = useReducedMotion();
  const { pathname } = useLocation();

  useEffect(() => {
    if (reduce) return undefined;

    const lenis = new Lenis({
      lerp: 0.11,
      wheelMultiplier: 1,
      smoothWheel: true,
      // smoothTouch laissé à false : le momentum natif mobile est meilleur.
    });
    window.__lenis = lenis;

    // PONT COMPAT (pattern Lenis standard) : Lenis ne dispatche PAS d'event
    // `scroll` natif. On en ré-émet un à chaque tick pour que TOUS les
    // composants legacy qui écoutent window 'scroll' (Header, BackToTop,
    // MegaMenu, barre de lecture article, LogoIntro, scroll-spy…) continuent
    // de fonctionner. Sans danger : pendant son propre scroll Lenis a
    // isScrolling=true et ignore ces events (pas de boucle de feedback) ; la
    // position lue == sa position animée, donc aucun re-sync parasite.
    const emitNativeScroll = () => window.dispatchEvent(new Event('scroll'));
    lenis.on('scroll', emitNativeScroll);

    // 🔴 RECALCUL DE HAUTEUR — sans lui, les ancres ne mènent nulle part.
    //
    // Lenis mesure la hauteur du document À SON INITIALISATION et s'en tient
    // là. Or nos pages grandissent APRÈS : widgets 3D en lazy, outils montés
    // au fil de l'eau, images qui arrivent. Lenis garde donc une limite
    // périmée et ÉCRASE toute cible au-delà.
    //
    // Mesuré en production sur /outils-gratuits : `lenis.limit` = 2939 alors
    // que le document fait 11315 px. Un clic sur la tuile « BUDGET · 3 outils »
    // (cible à 4325 px) laissait scrollY à 0 — le hash changeait, la page ne
    // bougeait pas. C'est le « quand je clique sur les outils ça ne change pas
    // d'outils » de Jade. Après `lenis.resize()`, la limite passe à 10595 et
    // l'ancre fonctionne.
    //
    // Le défaut touche TOUTE page dont le contenu s'allonge après le premier
    // rendu, pas seulement les outils — et il est muet : aucune erreur, le
    // clic « marche », il ne se passe simplement rien.
    const observer = new ResizeObserver(() => lenis.resize());
    observer.observe(document.body);

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    // Ancres internes → scroll fluide piloté par Lenis (offset = header sticky).
    const onClick = (e) => {
      // Si un handler propre a déjà géré le clic (sommaires qui scrollent
      // eux-mêmes via Lenis, etc.), ne pas double-scroller.
      if (e.defaultPrevented) return;
      const a = e.target?.closest?.('a[href^="#"]');
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href || href.length < 2) return;
      const target = document.querySelector(href);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -90 });
      // a11y : déplace aussi le focus vers la cible (sinon on casse le
      // skip-link « Aller au contenu » et la navigation clavier des ancres).
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (history.replaceState) history.replaceState(null, '', href);
    };
    document.addEventListener('click', onClick);

    return () => {
      document.removeEventListener('click', onClick);
      observer.disconnect();
      cancelAnimationFrame(rafId);
      lenis.destroy();
      delete window.__lenis;
    };
  }, [reduce]);

  // 2026-07-21 — cet effet forçait Lenis en haut à chaque navigation et
  // ÉCRASAIT la restauration de position faite par Layout (il s'exécutait
  // après, et Lenis pilote le scroll : il gagne toujours). Layout appelle
  // désormais `allerA()`, qui passe par Lenis quand il est actif — la remise
  // à zéro d'une nouvelle page y est déjà faite, au bon moment et seulement
  // quand c'est un vrai départ. Voir src/utils/scrollMemory.js.

  return null;
}
